export function getCurrentCheckpoint({ checkpoints, completedDistance }) {
  return (
    checkpoints.find((checkpoint) => Number(checkpoint.distanceKm) > completedDistance) ||
    checkpoints[checkpoints.length - 1] ||
    null
  );
}

export function getCheckpointState({ checkpoint, completedDistance, currentCheckpointId }) {
  if (Number(checkpoint.distanceKm) <= completedDistance) return 'done';
  if (checkpoint.id === currentCheckpointId) return 'current';
  return 'locked';
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function normalizeCheckpoint(checkpoint) {
  const lat = numberOrNull(checkpoint.lat ?? checkpoint.latitude);
  const lng = numberOrNull(checkpoint.lng ?? checkpoint.longitude);

  return {
    ...checkpoint,
    name: checkpoint.name || checkpoint.title || checkpoint.label || '',
    location: checkpoint.location || checkpoint.province || checkpoint.region || '',
    // Accept distanceKm, distance_km, or base_distance_km (library content-type field name)
    distanceKm:
      numberOrNull(checkpoint.distanceKm ?? checkpoint.distance_km ?? checkpoint.base_distance_km) || 0,
    lat,
    lng,
    imageUrl: checkpoint.imageUrl || checkpoint.image_url || '',
    medalName: checkpoint.medalName || checkpoint.medal_name || '',
    medalImageUrl: checkpoint.medalImageUrl || checkpoint.medal_image_url || '',
  };
}

function hasCoordinates(checkpoint) {
  return checkpoint.lat !== null && checkpoint.lng !== null;
}

export function recalculateCheckpointDistances(checkpoints = [], totalDistance = 0) {
  const numericTotal = Number(totalDistance);
  const maxCheckpointDistance = checkpoints.reduce(
    (max, checkpoint) => Math.max(max, Number(checkpoint.distanceKm) || 0),
    0
  );

  if (!Number.isFinite(numericTotal) || numericTotal <= 0 || maxCheckpointDistance <= 0) {
    return checkpoints;
  }

  const scale = numericTotal / maxCheckpointDistance;

  return checkpoints.map((checkpoint) => ({
    ...checkpoint,
    distanceKm: Math.round(((Number(checkpoint.distanceKm) || 0) * scale) * 100) / 100,
  }));
}

export function buildMapJourney({ relay, checkpoints = [], medals = [], scaleCheckpointsToTotal = false }) {
  const totalDistance = relay.totalRegisteredDistance || 0;
  const completedDistance = relay.totalCompletedDistance || 0;
  const rawCheckpoints = checkpoints.map(normalizeCheckpoint);
  const normalizedCheckpoints = scaleCheckpointsToTotal
    ? recalculateCheckpointDistances(rawCheckpoints, totalDistance)
    : rawCheckpoints;
  const currentCheckpoint = getCurrentCheckpoint({
    checkpoints: normalizedCheckpoints,
    completedDistance,
  });
  const progressRatio = totalDistance > 0 ? Math.min(completedDistance / totalDistance, 1) : 0;
  const mappedCheckpoints = normalizedCheckpoints.map((checkpoint) => ({
    ...checkpoint,
    state: getCheckpointState({
      checkpoint,
      completedDistance,
      currentCheckpointId: currentCheckpoint?.id,
    }),
  }));

  return {
    campaignId: relay.campaignId,
    campaignName: relay.campaignName,
    startLocation: relay.startLocation,
    endLocation: relay.endLocation,
    participants: relay.participants || [],
    totalDistance,
    completedDistance,
    progressRatio,
    currentCheckpoint:
      mappedCheckpoints.find((checkpoint) => checkpoint.id === currentCheckpoint?.id) ||
      mappedCheckpoints[mappedCheckpoints.length - 1] ||
      null,
    checkpoints: mappedCheckpoints,
    routePoints: mappedCheckpoints
      .filter(hasCoordinates)
      .map((checkpoint) => [checkpoint.lat, checkpoint.lng]),
    medals,
  };
}
